import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import SEOHead from "@/components/SEOHead";
import CTASection from "@/components/home/CTASection";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Lock, Sun, User, Camera, Sparkles, Heart, PartyPopper, Megaphone, Video, Building2, Users, Briefcase } from "lucide-react";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import GallerySlider from "@/components/home/GallerySlider";
import DesktopGallery from "@/components/home/DesktopGallery";
import MobileGalleryLightbox from "@/components/home/MobileGalleryLightbox";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

import heroImgDesktop from "@/assets/hero-sesje-desktop-new.webp";
import heroImgMobile from "@/assets/hero-sesje-mobile.webp";
import sesjeBiz1 from "@/assets/sesje-biz-1.webp";
import sesjeBiz2 from "@/assets/sesje-biz-2.webp";
import sesjeBiz3 from "@/assets/sesje-biz-3.webp";
import sesjeBiz4 from "@/assets/sesje-biz-4.webp";
import sesjeBiz5 from "@/assets/sesje-biz-5.webp";
import sesjeBiz6 from "@/assets/sesje-biz-6.webp";
import sesjeBiz7 from "@/assets/sesje-biz-7.webp";
import sesjeBiz8 from "@/assets/sesje-biz-8.webp";
import sesjeBiz9 from "@/assets/sesje-biz-9.webp";
import sesjeBiz10 from "@/assets/sesje-biz-10.webp";

const allSesjeImages = [
  { src: sesjeBiz3, alt: "Sesja wizerunkowa — pełna postać" },
  { src: sesjeBiz2, alt: "Portret wizerunkowy — męski portret" },
  { src: sesjeBiz4, alt: "Sesja biznesowa — kobieta z laptopem" },
  { src: sesjeBiz7, alt: "Sesja biznesowa — kobieta z telefonem" },
  { src: sesjeBiz5, alt: "Portret biznesowy — kobieta w okularach" },
  { src: sesjeBiz6, alt: "Sesja wizerunkowa — męski portret z uśmiechem" },
  { src: sesjeBiz1, alt: "Sesja biznesowa — elegancki portret męski" },
  { src: sesjeBiz8, alt: "Sesja glamour — czerwony garnitur" },
  { src: sesjeBiz9, alt: "Portret biznesowy — ciemny żakiet" },
  { src: sesjeBiz10, alt: "Sesja modowa — brązowa stylizacja" },
] as const;

const featuredSesje: [{ src: string; alt: string }, { src: string; alt: string }, { src: string; alt: string }] = [
  { src: sesjeBiz3, alt: "Sesja wizerunkowa — pełna postać" },
  { src: sesjeBiz8, alt: "Sesja glamour — czerwony garnitur" },
  { src: sesjeBiz4, alt: "Sesja biznesowa — kobieta z laptopem" },
];

const timelineSteps = [
  {
    number: "01",
    icon: Lock,
    title: "Przestrzeń na wyłączność",
    bullets: [
      "Cały lokal zarezerwowany wyłącznie na Twoją sesję",
      "Elastyczne ustawienie mebli i rekwizytów pod kadr",
      "Brak ograniczeń czasowych — pracujesz w swoim tempie",
    ],
  },
  {
    number: "02",
    icon: Sun,
    title: "Światło i klimat",
    bullets: [
      "Duże okna z naturalnym światłem dziennym",
      "Oświetlenie sztuczne — możliwość regulacji nastroju",
      "Różnorodne tła: bar, kominki, sofy, antresola, lustro",
    ],
  },
  {
    number: "03",
    icon: User,
    title: "Wsparcie na miejscu",
    bullets: [
      "Koordynator pomagający z organizacją sesji",
      "Opcjonalna fotografka lub operator kamery za dodatkową opłatą",
      "Garderoba do przygotowań i zmian stylizacji",
    ],
  },
];

const faqItems = [
  { q: "Czy mogę przyjść z własnym fotografem?", a: "Tak — możesz pracować z własnym fotografem lub operatorem kamery. Zapewniamy przestrzeń, a zespół dobierasz sam. Na życzenie możemy polecić sprawdzoną fotografkę współpracującą ze Studio Skandal." },
  { q: "Jak długo trwa minimalna rezerwacja?", a: "Minimalna rezerwacja to 1 godzina. Większość sesji trwa 2–4 godziny, ale czas dostosowujemy do Twoich potrzeb." },
  { q: "Czy są dostępne rekwizyty i meble do ustawienia?", a: "Tak — w lokalu znajdziesz sofy, fotele, stoliki, lustra, obrazy i inne elementy dekoracyjne. Meble można przestawiać i aranżować pod konkretny kadr." },
  { q: "Czy można kręcić wideo?", a: "Oczywiście — lokal doskonale sprawdza się zarówno do sesji zdjęciowych, jak i produkcji wideo: spotów reklamowych, teledysków, content na social media." },
  { q: "Jak wygląda rezerwacja terminu?", a: "Skontaktuj się z nami przez formularz lub telefon. Ustalimy datę, czas trwania i szczegóły sesji. Potwierdzenie rezerwacji następuje po wpłacie zaliczki." },
  { q: "Jak długo czeka się na gotowe zdjęcia przy sesjach biznesowych i prywatnych?", a: "Czas oczekiwania na gotowe zdjęcia zależy od rodzaju sesji i ustaleń w umowie, jednak standardowy proces postprodukcji zajmuje od 7 do 14 dni roboczych." },
];

const offerCards = [
  {
    icon: Building2,
    title: "Wynajem studia",
    subtitle: "Dla fotografów i twórców",
    description: "Klimatyczne studio fotograficzne w centrum Warszawy z gotowymi scenografiami i stylowymi wnętrzami. Idealne miejsce na sesje portretowe, fashion, sensualne oraz projekty reklamowe.",
    cta: "Wynajmij studio",
    ctaLink: "/wynajem-studia",
  },
  {
    icon: Users,
    title: "Sesje prywatne",
    subtitle: "Dla osób prywatnych i twórców",
    description: "Portrety, sesje wizerunkowe, kobiece i lifestyle w wyjątkowej przestrzeni z charakterem. Naturalne wnętrza i elegancki klimat sprawiają, że zdjęcia wyglądają stylowo i autentycznie.",
    cta: "Umów sesję",
    ctaLink: "/kontakt",
  },
  {
    icon: Briefcase,
    title: "Sesje biznesowe",
    subtitle: "Dla firm i marek osobistych",
    description: "Profesjonalne zdjęcia wizerunkowe do LinkedIn, stron internetowych i materiałów marketingowych. Stylowe wnętrza nadają zdjęciom elegancki i nowoczesny charakter.",
    cta: "Umów sesję",
    ctaLink: "/kontakt",
  },
];

const sessionTypes = [
  {
    icon: Camera,
    title: "Sesja biznesowa i wizerunkowa",
    description: "Profesjonalne zdjęcia dla przedsiębiorców, ekspertów i osób budujących markę osobistą. Idealne do LinkedIna, strony internetowej, prezentacji, artykułów i materiałów PR. W Studio Skandal w centrum Warszawy powstają zdjęcia, które budują wiarygodny i nowoczesny wizerunek.",
  },
  {
    icon: Sparkles,
    title: "Sesje modowe",
    description: "Studio fotograficzne idealne do realizacji sesji modowych, lookbooków i kampanii odzieżowych. Przestrzeń pozwala pracować zarówno przy prostych sesjach katalogowych, jak i bardziej kreatywnych projektach. Świetne miejsce dla projektantów, stylistów i modeli budujących portfolio.",
  },
  {
    icon: Heart,
    title: "Sesje glamour i beauty",
    description: "Eleganckie portrety podkreślające styl, urodę i charakter. Idealne dla osób, które chcą mieć profesjonalne zdjęcia do portfolio lub social mediów. Klimatyczne wnętrza studia pozwalają stworzyć wyjątkową atmosferę i efektowne kadry.",
  },
  {
    icon: PartyPopper,
    title: "Sesje lifestyle",
    description: "Swobodne, autentyczne zdjęcia pokazujące osobowość i emocje. Idealne dla twórców internetowych, par oraz osób budujących swój wizerunek w mediach społecznościowych. Komfortowa przestrzeń studia pozwala pracować spokojnie i w naturalnej atmosferze.",
  },
  {
    icon: Megaphone,
    title: "Sesje reklamowe i produktowe",
    description: "Profesjonalne zdjęcia produktów i materiałów reklamowych dla marek oraz sklepów internetowych. Idealne do kampanii marketingowych, katalogów i komunikacji w social mediach. Dobrze zaplanowana sesja pomaga wyróżnić produkt i zwiększyć sprzedaż.",
  },
  {
    icon: Video,
    title: "Nagrania wideo i rolki",
    description: "Studio w centrum Warszawy sprawdza się także przy nagraniach wideo. Idealne miejsce do tworzenia rolek, wywiadów, podcastów wideo oraz materiałów promocyjnych. Estetyczna przestrzeń i dobre światło pozwalają szybko nagrać profesjonalny content.",
  },
];

type AnimFn = (delay?: number) => Record<string, unknown>;

const Index = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const anim: AnimFn = (delay = 0) =>
    isMobile
      ? { initial: false as const, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } }
      : { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5, delay } };

  return (
    <PageTransition><div className="min-h-screen bg-background">
      <SEOHead title="Studio Skandal Foto — Studio Fotograficzne w Centrum Warszawy" description="Studio fotograficzne w centrum Warszawy. Wynajem studia, sesje biznesowe i prywatne. Klimatyczne wnętrza i stylowe scenografie." />
      <main>
        {/* 1. HERO */}
        <section ref={heroRef} className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
          <motion.div className="absolute inset-0 z-0" style={isMobile ? {} : { y: heroY, scale: heroScale }}>
            <img src={isMobile ? heroImgMobile : heroImgDesktop} alt="Studio Skandal — studio fotograficzne w centrum Warszawy" loading="eager" decoding="sync" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
          </motion.div>
          <div className="absolute inset-0 z-[1] bg-background/65" />
          <div className="absolute inset-0 z-[2] bg-gradient-to-t from-background via-background/20 to-background/50" />
          <motion.div style={isMobile ? {} : { opacity: contentOpacity }} className="relative z-10 flex flex-col items-center text-center px-4 sm:px-6 max-w-4xl mx-auto w-full">
            <motion.p
              className="text-primary font-semibold text-base sm:text-lg md:text-xl mb-4"
              initial={isMobile ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Studio fotograficzne w centrum Warszawy
            </motion.p>
            <motion.div
              className="mb-6"
              initial={isMobile ? false : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white"
                style={{
                  textShadow: '0 0 40px hsl(38 50% 55% / 0.3), 0 0 80px hsl(38 50% 55% / 0.15), 0 2px 12px rgba(0,0,0,0.5)',
                }}
              >
                Wyjątkowa przestrzeń{" "}
                <span className="text-gradient-primary">do zdjęć i video.</span>
              </h1>
            </motion.div>
            <motion.p
              className="text-base sm:text-lg md:text-xl text-white/70 max-w-2xl mx-auto drop-shadow-[0_1px_8px_rgba(0,0,0,0.4)]"
              initial={isMobile ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Stylowe wnętrza, scenografia i klimatyczna przestrzeń idealna do sesji biznesowych, prywatnych oraz kreatywnego contentu.
            </motion.p>

            {/* Two Buttons */}
            <motion.div
              className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
              initial={isMobile ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button asChild size="lg" className="rounded-xl text-base px-8">
                <Link to="/kontakt">Umów sesję</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-xl text-base px-8 bg-white/5 border-white/20 text-white hover:bg-white/10">
                <Link to="/wynajem-studia">Wynajmij studio</Link>
              </Button>
            </motion.div>

            {/* Info line */}
            <motion.div
              className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-white/40 text-xs sm:text-sm"
              initial={isMobile ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <span>Sesje biznesowe i prywatne</span>
              <span className="hidden sm:inline">·</span>
              <span>Wynajem studia fotograficznego</span>
              <span className="hidden sm:inline">·</span>
              <span>Rolki i content do social media</span>
            </motion.div>

            <div className="mt-10 flex flex-col items-center cursor-pointer" onClick={() => document.getElementById('about-sesje')?.scrollIntoView({ behavior: 'smooth' })}>
              <div className="w-[8px] h-[8px] rounded-full bg-primary animate-scroll-dot-fall" style={{ boxShadow: '0 0 12px 4px hsl(38 40% 60% / 0.6), 0 0 24px 8px hsl(38 40% 60% / 0.3)' }} />
            </div>
          </motion.div>
        </section>

        {/* 2. SEKCJA 1 — Studio stworzone do wyjątkowych kadrów */}
        <section id="about-sesje" className="py-12 sm:py-20 md:py-28 relative overflow-hidden bg-background">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/3 rounded-full blur-[80px] pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 md:px-12 relative z-10">
            <div className="max-w-3xl mx-auto">
              <motion.div {...anim()}>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-5">
                  Studio stworzone do{" "}
                  <span className="text-gradient-primary">wyjątkowych kadrów</span>
                </h2>
                <p className="text-white/50 text-base sm:text-lg leading-relaxed">
                  Studio Skandal to przestrzeń fotograficzna w centrum Warszawy zaprojektowana tak, aby każdy fragment wnętrza mógł stać się planem zdjęciowym. Stylowe meble, dekoracyjne ściany i dopracowane detale tworzą naturalne tło do sesji zdjęciowych oraz nagrań video.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 3. OFERTA — 3 KARTY (Biznesowe, Prywatne, Wynajem) */}
        <section className="py-10 md:py-20 relative bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {offerCards.map((card, i) => {
                const CardIcon = card.icon;
                return (
                  <motion.div
                    key={card.title}
                    {...anim(i * 0.1)}
                    className="relative p-6 md:p-8 rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] hover:border-primary/30 transition-all duration-500 hover:shadow-[0_0_60px_-15px_hsl(var(--primary)/0.3)] overflow-hidden flex flex-col"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] pointer-events-none" />
                    <div className="relative z-10 flex flex-col flex-1">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
                        <CardIcon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl md:text-2xl font-extrabold text-foreground mb-1">{card.title}</h3>
                      <p className="text-primary text-sm font-medium mb-3">{card.subtitle}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1">{card.description}</p>
                      <Button asChild className="w-full rounded-xl">
                        <Link to={card.ctaLink}>{card.cta}</Link>
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 4. WYRÓŻNIENIE CENOWE – ukryte tymczasowo */}
        {/*
        <section className="py-10 md:py-20 relative bg-background">
          <div className="container mx-auto px-4">
            <motion.div {...anim()} className="max-w-xl mx-auto text-center p-8 md:p-12 rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-primary/30 shadow-[0_0_60px_-15px_hsl(var(--primary)/0.3)] relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-transparent to-primary/[0.03] pointer-events-none" />
              <div className="relative z-10">
                <p className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-primary drop-shadow-[0_0_24px_hsl(var(--primary)/0.4)] mb-3">
                  od 200 PLN / godzina
                </p>
                <p className="text-muted-foreground text-sm md:text-base">
                  Cena netto. Wynajem na godziny — elastycznie dopasowany do Twojej sesji.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
        */}

        {/* 5. BENTO GALLERY */}
        <section className="py-10 md:py-24 overflow-hidden bg-background">
          <div className="px-4 sm:px-6 lg:px-8 mb-10 md:mb-14">
            <motion.span {...anim()} className="text-base md:text-lg font-medium text-primary-light tracking-wide drop-shadow-[0_0_12px_hsl(var(--primary)/0.5)] block mb-2">
               Sesje biznesowe, prywatne i sensualne w eleganckich wnętrzach studia
             </motion.span>
             <motion.h2 {...anim(0.05)} className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
               Zobacz, jak wyglądają sesje zdjęciowe w Studio Skandal Foto w centrum Warszawy
             </motion.h2>
          </div>
          <motion.div {...anim(0.1)} className="px-4 sm:px-6 lg:px-8">
            {isMobile ? (
              <>
              <div className="grid grid-cols-2 gap-2">
                  {allSesjeImages.map((img, i) => (
                    <div key={i} className="aspect-[2/3] rounded-2xl overflow-hidden cursor-pointer" onClick={() => setLightboxIndex(i)}>
                      <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
                {lightboxIndex !== null && (
                  <MobileGalleryLightbox images={allSesjeImages} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
                )}
              </>
            ) : (
              <DesktopGallery featured={featuredSesje} all={allSesjeImages} sliderStartIndex={3} featuredAspect="aspect-[2/3]" carouselAspect="aspect-[2/3]" />
            )}
          </motion.div>
        </section>

        {/* MEGA-SEKCJA: Rodzaje sesji + Co dostajesz + FAQ */}
        <section className="py-10 md:py-24 bg-background relative overflow-hidden">
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-primary/3 rounded-full blur-[100px] pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-10 md:mb-16">
              <motion.h2 {...anim()} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-4">
                Studio fotograficzne w centrum Warszawy —{" "}
                <span className="text-gradient-primary">wiele możliwości w jednym miejscu</span>
              </motion.h2>
              <motion.p {...anim(0.05)} className="text-white/50 text-base sm:text-lg leading-relaxed max-w-3xl">
                Studio Skandal to wyjątkowe studio fotograficzne w centrum Warszawy oferujące wynajem studia, sesje biznesowe oraz kreatywne sesje zdjęciowe i nagrania video. Klimatyczne wnętrza i stylowe scenografie pozwalają realizować różne projekty fotograficzne i content do social media.
              </motion.p>
            </div>

            {/* Rodzaje sesji — 6 kafelków z opisami */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {sessionTypes.map((chip, i) => {
                const ChipIcon = chip.icon;
                return (
                  <motion.div
                    key={chip.title}
                    {...anim(i * 0.05)}
                    className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-primary/30 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <ChipIcon className="w-5 h-5 text-primary shrink-0" />
                      <span className="text-sm font-semibold text-foreground">{chip.title}</span>
                    </div>
                    <p className="text-white/40 text-xs leading-relaxed">{chip.description}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="w-24 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent mx-auto my-12 md:my-20" />

            {/* Co dostajesz — zigzag timeline */}
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 -translate-x-px top-0 bottom-0 w-px bg-border/50" />
              <div className="md:hidden absolute left-6 top-0 bottom-0 w-px bg-border/50" />

              <div className="flex flex-col gap-4 md:gap-24">
                {timelineSteps.map((step, i) => {
                  const isLeft = i % 2 === 0;
                  const StepIcon = step.icon;

                  return (
                    <motion.div key={step.number} className="relative" {...anim(i * 0.1)}>
                      <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-4 w-12 h-12 md:w-14 md:h-14 rounded-full bg-card border border-primary/30 flex items-center justify-center z-10">
                        <span className="text-lg md:text-xl font-extrabold text-primary">{step.number}</span>
                      </div>

                      <div className={`pl-16 md:pl-0 ${isLeft ? "md:w-1/2 md:pr-10" : "md:w-1/2 md:ml-auto md:pl-10"}`}>
                        <div className="p-4 md:p-10 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 transition-all duration-500 hover:border-primary/30 hover:shadow-premium relative overflow-hidden">
                          <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-5">
                              <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                                <StepIcon className="w-5 h-5 text-primary" />
                              </div>
                              <h3 className="text-xl md:text-2xl font-extrabold text-foreground">{step.title}</h3>
                            </div>
                            <ul className="space-y-2.5 mb-5">
                              {step.bullets.map((bullet, j) => (
                                <li key={j} className="flex items-start gap-2.5 text-sm text-muted-foreground/90">
                                  <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                  <span>{bullet}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div className="w-24 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent mx-auto my-12 md:my-20" />

            {/* FAQ */}
            <div className="max-w-3xl mx-auto">
              <motion.div {...anim(0.1)}>
                <Accordion type="single" collapsible className="space-y-2.5 md:space-y-3">
                  {faqItems.map((item, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="rounded-xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm px-4 md:px-6 py-1 data-[state=open]:bg-white/[0.05]"
                    >
                      <AccordionTrigger className="text-left text-sm md:text-lg font-medium text-white hover:text-primary transition-colors [&[data-state=open]]:text-primary">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-white/50 text-sm md:text-base leading-relaxed pb-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SEO: O nas / O usługach */}
        <section className="py-16 md:py-24 bg-background relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              <motion.div {...anim(0.1)}>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white mb-6">
                  Kompleksowe Usługi Fotograficzne i Realizacje Wizualne
                </h2>
                <p className="text-white/50 text-base md:text-lg leading-relaxed">
                  Studio Skandal Foto to wiodące studio fotograficzne specjalizujące się w dostarczaniu najwyższej jakości materiałów wizualnych dla klientów indywidualnych oraz biznesowych. Proces profesjonalnej fotografii obejmuje zaawansowane planowanie, wykorzystanie sprzętu najwyższej klasy oraz profesjonalną postprodukcję obrazu (retusz). Naszym celem jest tworzenie zdjęć, które spełniają rygorystyczne standardy branżowe oraz skutecznie komunikują wartości wizualne.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SEO: Dlaczego my? */}
        <section className="py-16 md:py-24 bg-background relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              <motion.div {...anim(0.1)}>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white mb-6">
                  Dlaczego warto wybrać Studio Skandal Foto?
                </h2>
                <p className="text-white/50 text-base md:text-lg leading-relaxed mb-8">
                  Wybór odpowiedniego fotografa jest kluczowy dla ostatecznego efektu sesji. Nasze usługi charakteryzują się:
                </p>
                <ul className="space-y-5">
                  {[
                    { title: "Profesjonalny sprzęt fotograficzny", desc: "Korzystamy wyłącznie z najnowocześniejszych aparatów, obiektywów i systemów oświetleniowych, co gwarantuje najwyższą rozdzielczość i dynamikę tonalną zdjęć." },
                    { title: "Doświadczenie i precyzja", desc: "Posiadamy bogate portfolio zrealizowanych projektów, co pozwala nam na bezbłędne dopasowanie technik fotograficznych do specyfiki zlecenia." },
                    { title: "Zaawansowany retusz i postprodukcja", desc: "Każde zdjęcie przechodzi rygorystyczny proces edycji cyfrowej, mający na celu optymalizację kolorów, kontrastu i detali." },
                    { title: "Szybki czas realizacji", desc: "Rozumiemy, jak cenny jest czas, dlatego dostarczamy gotowe, obrobione materiały w najkrótszym możliwym terminie." },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-primary/50 mt-2.5 flex-shrink-0" />
                      <div>
                        <span className="text-white font-semibold text-base md:text-lg">{item.title}:</span>{" "}
                        <span className="text-white/50 text-base md:text-lg leading-relaxed">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SEO: Zakres usług */}
        <section className="py-16 md:py-24 bg-background relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              <motion.div {...anim(0.1)}>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white mb-6">
                  Zakres Naszych Usług Fotograficznych
                </h2>
                <p className="text-white/50 text-base md:text-lg leading-relaxed mb-8">
                  Oferujemy szeroki wachlarz usług, dostosowanych do indywidualnych potrzeb:
                </p>
                <ul className="space-y-5">
                  {[
                    { title: "Wynajem studia dla fotografek i fotografów", desc: "Studio Skandal Foto to przestrzeń na wyłączność dla profesjonalistów, do wynajęcia na godziny." },
                    { title: "Fotografia Biznesowa i Wizerunkowa", desc: "Profesjonalne portrety dla firm, na profile LinkedIn i do materiałów PR." },
                    { title: "Fotografia Produktowa i Reklamowa", desc: "Wysokiej jakości zdjęcia produktów (packshoty), niezbędne do skutecznej sprzedaży w e-commerce i katalogach." },
                    
                    { title: "Sesje Studyjne i Plenerowe", desc: "Indywidualne sesje zdjęciowe dopasowane do kreatywnych wymagań klienta." },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-primary/50 mt-2.5 flex-shrink-0" />
                      <div>
                        <span className="text-white font-semibold text-base md:text-lg">{item.title}:</span>{" "}
                        <span className="text-white/50 text-base md:text-lg leading-relaxed">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SEO SENTENCE */}
        <section className="py-10 md:py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <p className="text-white/30 text-sm md:text-base text-center max-w-3xl mx-auto leading-relaxed">
              Studio Skandal foto to klimatyczne studio fotograficzne w centrum Warszawy dostępne na wynajem dla fotografów oraz klientów realizujących sesje biznesowe i prywatne.
            </p>
          </div>
        </section>

        {/* GALLERY SLIDER */}
        <section className="py-12 md:py-16 overflow-hidden bg-background">
          <motion.div {...anim(0.1)}>
            <GallerySlider aspectRatio="aspect-[3/4]" images={[
              { src: sesjeBiz1, alt: "Sesja biznesowa — elegancki portret męski" },
              { src: sesjeBiz2, alt: "Portret wizerunkowy — męski portret" },
              { src: sesjeBiz3, alt: "Sesja wizerunkowa — pełna postać" },
              { src: sesjeBiz4, alt: "Sesja biznesowa — kobieta z laptopem" },
              { src: sesjeBiz5, alt: "Portret biznesowy — kobieta w okularach" },
              { src: sesjeBiz6, alt: "Sesja wizerunkowa — męski portret z uśmiechem" },
              { src: sesjeBiz7, alt: "Sesja biznesowa — kobieta z telefonem" },
              { src: sesjeBiz8, alt: "Sesja glamour — czerwony garnitur" },
              { src: sesjeBiz9, alt: "Portret biznesowy — ciemny żakiet" },
              { src: sesjeBiz10, alt: "Sesja modowa — brązowa stylizacja" },
            ] as const} />
          </motion.div>
        </section>

        {/* CTA */}
        <CTASection />
      </main>
      <Footer />
    </div></PageTransition>
  );
};

export default Index;
